import React from 'react';
import { Dropdown as AntdDropdown } from 'antd';
import type { DropDownProps } from 'antd/lib/dropdown';
import type { DropdownButtonProps } from 'antd/lib/dropdown/dropdown-button';
import { css } from '@emotion/react';
import styled from 'types/theme.types';
import { newDesignOnly } from 'constants/themes.constants';

type MenuCapProps = {
  /** Caps the menu and scrolls it, in place of an inline `style` on `menu`. */
  menuMaxHeight?: number;
};

export type BridgedDropdownProps = DropDownProps & MenuCapProps;

/**
 * The import seam for raw antd `Dropdown` call sites: `components/common/dropdown` builds its own Button
 * trigger from `buttonTextId`, so it cannot take one over without restructuring the DOM. It also carries
 * the `text-align: left` v4 patch, which v5 dropped and has no token — without an owner, a dropdown
 * inside a centred container centres its items.
 */
const Overlay: React.FC<MenuCapProps & { children: React.ReactNode }> = ({ menuMaxHeight, children }) => (
  <MenuContainer $maxHeight={menuMaxHeight}>{children}</MenuContainer>
);

Overlay.displayName = 'BridgedDropdownOverlay';

function composeOverlay(menuMaxHeight?: number, dropdownRender?: DropDownProps['dropdownRender']) {
  return function overlay(originNode: React.ReactNode) {
    return <Overlay menuMaxHeight={menuMaxHeight}>{dropdownRender ? dropdownRender(originNode) : originNode}</Overlay>;
  };
}

const BridgedDropdownRoot: React.FC<BridgedDropdownProps> = ({ menuMaxHeight, dropdownRender, ...props }) => (
  <AntdDropdown {...props} dropdownRender={composeOverlay(menuMaxHeight, dropdownRender)} />
);

// `Dropdown.Button` forwards `dropdownRender` to the Dropdown it wraps, so the same overlay applies.
// Attached rather than re-exported so a call site can drop the raw antd import wholesale.
const BridgedDropdownButton: React.FC<DropdownButtonProps & MenuCapProps> = ({
  menuMaxHeight,
  dropdownRender,
  ...props
}) => <AntdDropdown.Button {...props} dropdownRender={composeOverlay(menuMaxHeight, dropdownRender)} />;

export const BridgedDropdown = Object.assign(BridgedDropdownRoot, { Button: BridgedDropdownButton });

const MenuContainer = styled.div<{ $maxHeight?: number }>`
  ${({ $maxHeight }) =>
    $maxHeight !== undefined &&
    css`
      // The cap has to sit on the menu, not on this wrapper: antd puts the panel's box-shadow on
      // .ant-dropdown-menu, and an overflow other than visible on an ancestor clips it.
      .ant-dropdown-menu {
        max-height: ${$maxHeight}px;
        overflow-y: auto;
      }
    `}

  ${newDesignOnly(css`
    // v4 had it, v5 does not, and there is no token for it.
    // https://github.com/ant-design/ant-design/blob/4.x-stable/components/dropdown/style/index.less#L122
    text-align: left;
  `)}
`;

export default BridgedDropdown;
